// src/services/chatMessagingAPI.js
//
// Human-to-human chat (Patient↔Doctor, Patient↔Navigator, Navigator↔Doctor).
// Separate from chatService.js (Ask Previu) — different backend
// controller, different data shape, no Claude involved at all.
//
// SSE + JWT note: native browser EventSource cannot send custom headers, so
// it can't carry an Authorization: Bearer token — and every endpoint here
// requires one. Rather than invent a new workaround, subscribeToConversation()
// below reuses the EXACT fetch()-with-Authorization-header +
// response.body.getReader() pattern chatService.js already uses for the AI
// chat stream (see parseStream() there) — same proven approach, just parsing
// Spring's SseEmitter frame shape (event: message / data: {...}) instead of
// Anthropic's.

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

function authHeaders() {
  const jwt = localStorage.getItem('previu_token');
  return {
    'Content-Type': 'application/json',
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
}

async function handle(response) {
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      // Session expired — same convention as chatService.js's sendChatMessage
      localStorage.removeItem('previu_token');
      window.location.href = '/login?reason=session_expired';
      throw new Error('Session expired');
    }
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.message || `Request failed (${response.status})`);
  }
  if (response.status === 204) return null;
  return response.json();
}

export const chatMessagingAPI = {
  /** conversationType: 'PATIENT_DOCTOR' | 'PATIENT_NAVIGATOR' | 'NAVIGATOR_DOCTOR'. counterpartId is always the OTHER party — your own id is resolved server-side from the JWT, never sent. */
  createOrGet: (conversationType, counterpartId) =>
    fetch(`${API_BASE}/conversations`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ conversationType, counterpartId }),
    }).then(handle),

  list: () =>
    fetch(`${API_BASE}/conversations`, { headers: authHeaders() }).then(handle),

  /**
   * Every staff member (and, for a navigator caller, every doctor with an
   * active login) the caller can message from Team Chat: [{ id, name, role,
   * staffRole, conversationType, conversationId }]. conversationId is null
   * when there is no thread yet.
   *
   * Deliberately NOT routed through handle(): a backend without this endpoint
   * answers 404 (or, depending on how security resolves an unmapped path,
   * 401/403), and handle() treats 401/403 as an expired session and logs the
   * user out. A missing directory must only mean "fall back to existing
   * threads" — TeamInboxPage does that on any rejection.
   */
  teamDirectory: () =>
    fetch(`${API_BASE}/conversations/team-directory`, { headers: authHeaders() })
      .then((response) => {
        if (!response.ok) throw new Error(`Team directory unavailable (${response.status})`);
        return response.json();
      }),

  /** conversationType: which relationship the caller wants to start — see backend controller's javadoc for which role+type combos are actually wired. */
  getEligibleCounterparts: (conversationType) =>
    fetch(`${API_BASE}/conversations/eligible-counterparts?conversationType=${encodeURIComponent(conversationType)}`, {
      headers: authHeaders(),
    }).then(handle),

  getMessages: (conversationId, page = 0, size = 30) =>
    fetch(`${API_BASE}/conversations/${conversationId}/messages?page=${page}&size=${size}`, {
      headers: authHeaders(),
    }).then(handle),

  sendMessage: (conversationId, body, attachmentHealthRecordId) =>
    fetch(`${API_BASE}/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ body, attachmentHealthRecordId: attachmentHealthRecordId || null }),
    }).then(handle),

  markRead: (conversationId) =>
    fetch(`${API_BASE}/conversations/${conversationId}/read`, {
      method: 'PATCH',
      headers: authHeaders(),
    }).then(handle),

  /**
   * subscribeToConversation — opens the SSE stream and calls onMessage(msg)
   * for every new message pushed. Returns an unsubscribe function — call it
   * on unmount (aborts the fetch, which the backend's ChatEventBroadcaster
   * detects via emitter.onError/onCompletion and cleans up its side too).
   */
  subscribeToConversation(conversationId, onMessage, onError) {
    const controller = new AbortController();
    const jwt = localStorage.getItem('previu_token');

    fetch(`${API_BASE}/conversations/${conversationId}/stream`, {
      headers: { Authorization: jwt ? `Bearer ${jwt}` : '' },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok || !response.body) {
          onError?.(new Error(`Stream failed (${response.status})`));
          return;
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            // DEF-034 — "SSE stream repeatedly cancels/reconnects without
            // backgrounding."
            //
            // A stream that ends CLEANLY (Railway's edge proxy closing an idle
            // connection, which this codebase has already hit — see
            // ChatEventBroadcaster's logging comment) lands here, not in the
            // catch below. The loop used to just `break`, the promise resolved,
            // and onError was never called — so the hook never rearmed and the
            // connection was silently dead until the tab was hidden and shown
            // again. Reporting it as an error is what lets the caller's backoff
            // reconnect, and combined with the server-side heartbeat the idle
            // closes that caused the churn no longer happen in the first place.
            onError?.(new Error('Stream closed by server'));
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop(); // keep incomplete line for next chunk

          for (const line of lines) {
            // SSE spec: a "data:" field may be followed by at most one
            // space before the value, and that space should be stripped —
            // but it's optional. Spring's SseEmitter (used by this
            // endpoint) writes "data:{json}" with NO space; the AI chat
            // endpoint this parser was originally modeled on hand-writes
            // "data: {json}" WITH a space. Checking for 'data:' alone and
            // stripping at most one following space handles both, rather
            // than silently dropping every frame from one of them.
            if (!line.startsWith('data:')) continue;
            let raw = line.slice(5);
            if (raw.startsWith(' ')) raw = raw.slice(1);
            raw = raw.trim();
            if (!raw) continue;
            try {
              onMessage(JSON.parse(raw));
            } catch {
              // malformed frame — skip rather than crash the whole stream
            }
          }
        }
      })
      .catch((e) => {
        if (e.name !== 'AbortError') onError?.(e);
      });

    return () => controller.abort();
  },
};
